import { useMemoizedContentGetter } from 'utils/contentful'
import { LinkButton, MediaImageStandard, Typography } from 'components/Contentful'
import Section, { SECTION_VARIANTS } from 'components/Section'
import { AchieveTheme, Grid } from '@achieve/ascend'
import { get as _get } from 'lodash-es'
import classNames from 'classnames'
import AppStoreButtons from 'components/AppStoreButtons/AppStoreButtons'

function MoloHero({ content }) {
  const {
    button_apple: appleButton,
    button_google: googleButton,
    copy,
    eyebrow,
    config,
    heroImage,
    image_apple: appleImage,
    image_google: googleImage,
    title,
    ctaButton,
  } = useMemoizedContentGetter(content, [
    'button_apple',
    'button_google',
    'copy',
    'eyebrow',
    'config',
    'heroImage',
    'image_apple',
    'image_google',
    'title',
    'ctaButton',
  ])

  const variation = config?.jsonContent?.variation || 'default'
  const heroImageContent = _get(heroImage, 'mediaContent', null)
  const imageAlt = _get(heroImage, 'mediaAltText', null)
  const ctaText = _get(ctaButton, 'linkText.json.content[0].content[0].value', '')
  const ctaHref = _get(ctaButton, 'linkHref', '')

  const variationsConfig = {
    default: {
      sectionVariant: SECTION_VARIANTS.bgShadowPrimaryHighlightBlue,
      sectionStyle: null,
      GridContentSize: 6,
      GridImageSize: 6,
      layoutImage: 'responsive',
      titleSizeVariantClassName: 'headingLg lg:displayXl !font-bold',
    },
    GoodApp: {
      sectionVariant: SECTION_VARIANTS.bgShadowPrimaryHighlightWhiteBlue,
      sectionStyle: 'section-good-app',
      GridContentSize: 5,
      GridImageSize: 7,
      layoutImage: 'responsive',
      titleSizeVariantClassName: 'displayXl !font-bold',
    },
    DebtCheckup: {
      sectionStyle: 'section-debt bg-blue-25',
      GridContentSize: 6,
      GridImageSize: 6,
      titleSizeVariantClassName: 'headingLg lg:displayXl !font-bold',
    },
  }

  return (
    <Section
      className={classNames(
        'relative overflow-hidden',
        ...[variationsConfig[variation].sectionStyle]
      )}
      childContainerClassName={
        'min-h-[540px] flex justify-center items-center [.section-good-app_&]:min-h-[617px] lg:[.section-debt_&]:items-center'
      }
      data-testid="molo-hero-section"
      data-narrow={true}
      variant={variationsConfig[variation].sectionVariant}
      contain
    >
      <Grid
        className={classNames(
          'relative h-full lg:[.section-debt_&]:h-[initial] lg:[.section-debt_&]:self-stretch lg:[.section-debt_&]:[position:initial]',
          'flex-col lg:flex-row'
        )}
        container
        justifyContent="center"
        alignItems={'initial'}
      >
        <Grid
          className={
            'p-6 lg:px-0 lg:py-6 [&>*:not(:last-child)]:mb-3 lg:[.section-debt_&]:flex lg:[.section-debt_&]:flex-col lg:[.section-debt_&]:self-center'
          }
          item
          xs={12}
          lg={variationsConfig[variation].GridContentSize}
        >
          {eyebrow && (
            <Typography
              content={eyebrow?.textContent}
              variant="bodySm"
              fontWeight="bold"
              component="h1"
              data-testid="molo-hero-eyebrow"
            />
          )}
          {title && (
            <Typography
              content={title?.textContent}
              color={AchieveTheme?.ascend?.colors?.blue[500]}
              responsive={true}
              className={variationsConfig[variation].titleSizeVariantClassName}
              breakpoint="lg"
              fontWeight="bold"
              component="h2"
              data-testid="molo-hero-title"
            />
          )}
          {copy && (
            <Typography
              content={copy?.textContent}
              responsive={true}
              variant="bodyLg"
              mobileVariant="bodySm"
              breakpoint="lg"
              fontWeight="normal"
              component="p"
              data-testid="molo-hero-copy"
            />
          )}
          {ctaButton && (
            <LinkButton
              track={{
                list_name: 'Home Hero CTA click',
                click_id: ctaText,
                click_text: ctaText,
                nav_link_section: 'Home Hero',
                track_event: 'internal_campaign_click',
                event_action: 'button_click',
                event_type: 'button_click',
              }}
              content={{
                linkHref: ctaHref,
                linkText: ctaText,
                uiComponent: _get(ctaButton, 'uiComponent', ''),
              }}
              typographicOptions={{ variant: 'bodyLg', fontWeight: 'bold' }}
              variant="contained"
              color="primary"
              className={classNames('', 'w-full lg:w-min')}
            />
          )}
          <div className={'h-[38px]'}>
            <AppStoreButtons
              navLinkSection="Hero"
              appleImage={appleImage}
              googleImage={googleImage}
              appleButton={appleButton}
              googleButton={googleButton}
              priority
            />
          </div>
        </Grid>

        {heroImageContent && (
          <Grid
            className={classNames(
              'relative lg:max-h-[inherit] lg:[.section-debt_&::before]:content-[""] lg:[.section-debt_&::before]:absolute w-[422.01px] lg:[.section-debt_&::before]:h-[441.9px] lg:[.section-debt_&::before]:left-[-20.2px] lg:[.section-debt_&::before]:gap-0 lg:[.section-debt_&::before]:rotate-[-0.01deg] lg:[.section-debt_&::before]:blur-[75px] lg:[.section-debt_&::before]:top-0 lg:[.section-debt_&::before]:[background:linear-gradient(180deg,rgb(25_56_219_/_20%)_14.68%,rgb(33_161_255_/_20%)_73.99%,rgb(254_206_47_/_20%)_98.44%)',
              'mb-12 lg:mb-0'
            )}
            item
            xs={12}
            lg={variationsConfig[variation].GridImageSize}
          >
            <HeroImage
              imageContent={heroImageContent}
              alt={imageAlt}
              layout={variationsConfig[variation].layoutImage}
              objectFit={variationsConfig[variation].objectFitImage}
            />
          </Grid>
        )}
      </Grid>
    </Section>
  )
}

function HeroImage({ imageContent, alt, layout, objectFit }) {
  return (
    <div
      className={classNames(
        'relative -translate-x-2/4 w-[197px] h-[394px] mb-6 left-2/4 lg:absolute lg:h-[600px] lg:w-[300px] lg:mb-0',
        '[.section-good-app_&]:h-[392px] [.section-good-app_&]:w-[197px] [.section-good-app_&]:mb-0 [.section-good-app_&]:top-0 [.section-good-app_&]:lg:h-[676px] [.section-good-app_&]:lg:w-[340px] [.section-good-app_&]:lg:mb-6 [.section-good-app_&]:lg:-top-12',
        '[.section-good-app_&]:[&_img]:object-top [.section-good-app_&]:[&_img]:lg:object-[center_center]',
        '[.section-debt_&]:h-[392px] [.section-debt_&]:w-full [.section-debt_&]:flex [.section-debt_&]:justify-center [.section-debt_&]:mb-0 [.section-debt_&]:top-0 [.section-debt_&]:lg:w-[370px] [.section-debt_&]:lg:h-full [.section-debt_&]:lg:mb-0',
        '[.section-debt_&]:[&_img]:object-top [.section-debt_&]:[&_img]:h-full [.section-debt_&]:[&_img]:lg:object-[center_center]'
      )}
    >
      {imageContent && (
        <MediaImageStandard
          content={imageContent}
          alt={alt}
          layout={layout}
          objectFit={objectFit}
          width={340}
          priority
        />
      )}
    </div>
  )
}
export { MoloHero }
export default MoloHero
